import { PrintElementOptions, printElement, PRINT_SECTION_ID, WITH_PRINTING_CLASS } from '@deltasierra/shared';
import PrintIcon from '@material-ui/icons/Print';
import * as React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import * as Chart from 'chart.js';
import { DSFabProps, DSFab } from '../../mui/Fab';

const GlobalStyle = createGlobalStyle<{ printPageWidth?: string; pageSize?: string }>`
    #${PRINT_SECTION_ID} {
        position: absolute;
        top: 0;
        left: 0;

        ${props =>
            props.printPageWidth
                ? `
            max-width: ${props.printPageWidth};
        `
                : ''}
    }

    @media screen {
        #${PRINT_SECTION_ID} {
            /* display: none; */
            visibility: hidden;
        }
    }

    @media print {
        body.${WITH_PRINTING_CLASS} > *:not(#${PRINT_SECTION_ID}),
        body.${WITH_PRINTING_CLASS} > *:not(#${PRINT_SECTION_ID}) *,
        .hide-print {
            visibility: hidden;
            display: none;
        }


        #${PRINT_SECTION_ID},
        #${PRINT_SECTION_ID} * {
            visibility: visible;
        }
        
    }

    ${props =>
        props.pageSize
            ? `
        @page {
            size: ${props.pageSize};
        }
    `
            : ''}
`;

const StyledDSFab = styled(DSFab)`
    ${({ theme }) => `
        color: ${theme.palette.primary.main};
        border: 1px solid ${theme.palette.primary.main};
        background-color: transparent;
        box-shadow: none;
        &:hover {
            color: #ffffff;
            background-color: ${theme.palette.primary.main};
        }
    `}
`;

export interface CustomPrintButtonProps extends Omit<DSFabProps, 'children'> {
    targetSelector: string;
    hideOnPrint?: boolean;
    printPageWidth?: string;
    pageSize?: string;
    printOptions?: PrintElementOptions;
}

// This print button has the default pass through props
export const CustomPrintButton: React.FC<CustomPrintButtonProps> = ({
    hideOnPrint = true,
    pageSize,
    printOptions,
    printPageWidth,
    targetSelector,
    ...props
}) => {
    const [isShowingPrintStyles, updateShowPrintStyles] = React.useState(false);
    const onClickPrint = () => {
        const elemToPrint = document.querySelector(targetSelector);
        if (elemToPrint) {
            updateShowPrintStyles(true);
            setTimeout(() => {
                printElement(elemToPrint, {
                    ...printOptions,
                    onAfterPrint: () => {
                        /*
                         * For some reason we need to delay this by 500ms
                         * Idk why but it works - everyone 2020
                         */
                        setTimeout(() => updateShowPrintStyles(false), 500);
                        printOptions?.onAfterPrint?.();
                    },
                });
            }, 1);
        } else {
            throw new Error(`Cannot find print element for selector: '${targetSelector}'`);
        }
    };
    return (
        <>
            {isShowingPrintStyles && <GlobalStyle pageSize={pageSize} printPageWidth={printPageWidth} />}
            <StyledDSFab className={hideOnPrint ? 'hide-print' : undefined} onClick={onClickPrint} {...props}>
                <PrintIcon />
            </StyledDSFab>
        </>
    );
};

export type PaperSizes = 'A4 Landscape' | 'A4' | 'Letter Landscape' | 'Letter';

type PaperSizeToPageWidthType = {
    [key in PaperSizes]: {
        size: string;
        width: string;
    };
};

export const PaperSizeToPageWidth: PaperSizeToPageWidthType = {
    A4: {
        size: 'A4',
        width: '210mm',
    },
    'A4 Landscape': {
        size: 'A4 landscape',
        width: '297mm',
    },
    Letter: {
        size: 'letter',
        width: '8.5in',
    },
    'Letter Landscape': {
        size: 'letter landscape',
        width: '11in',
    },
};

export interface PrintButtonProps extends Omit<CustomPrintButtonProps, 'hideOnPrint' | 'pageSize' | 'printPageWidth'> {
    targetSelector: string;
    paperSize?: PaperSizes;
    setPrintPageWidth?: boolean;
}

// This print button should be used to make it easy to keep customized printing within this file
export const PrintButton: React.FC<PrintButtonProps> = ({
    paperSize = 'A4',
    printOptions,
    setPrintPageWidth = true,
    ...props
}) => (
    <CustomPrintButton
        pageSize={PaperSizeToPageWidth[paperSize].size}
        printOptions={{
            moveDiv: true,
            onBeforePrint: () => {
                // By default, run the chartjs resizer
                Object.values(Chart.instances).forEach(chart => {
                    chart.resize();
                });
                printOptions?.onBeforePrint?.();
            },
            ...printOptions,
        }}
        printPageWidth={setPrintPageWidth ? PaperSizeToPageWidth[paperSize].width : undefined}
        {...props}
    />
);
